import { Components, Interfaces, Logger, Sendum, Services } from '@sendum/core'
import { removeMessageListener } from '@sendum/core/src/services/MQTT/MQTTActions'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { AnyAction } from 'redux'

import * as TimelineActions from './store/TimelineActions'
import { TimelineRootState } from './store/TimelineStore'
import s from './TimelineContainer.module.scss'
import { TimelineContent } from './TimelineContent'

const logger = new Logger('TimelineContent')

interface IProps {
    masterTrackingNo?: string
    shipmentId?: string
    withManufacturingLabels?: boolean
    onLoaded?: (isShowHeaderShadow: boolean, isShowFooterShadow: boolean) => void
    onScroll?: (event: React.UIEvent<HTMLDivElement>) => void
    showScrollShadow?: {
        top?: boolean
        bottom?: boolean
    }
    noDataText?: JSX.Element
    isSubWidget?: boolean
}

export const TimelineContainer = ({
    masterTrackingNo,
    shipmentId,
    withManufacturingLabels,
    onLoaded,
    onScroll,
    noDataText,
    showScrollShadow = { top: true, bottom: true },
    isSubWidget
}: IProps): JSX.Element => {
    const dispatch = useDispatch()
    const isLoading = useSelector<TimelineRootState, boolean | undefined>(
        (state: TimelineRootState) => state.Timeline.isLoading,
        shallowEqual
    )
    const [isShowHeaderShadow, setShowHeaderShadow] = useState<boolean>(false)
    const [isShowFooterShadow, setShowFooterShadow] = useState<boolean>(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const loadData = useCallback(() => {
        dispatch(
            (TimelineActions.getSegments(
                masterTrackingNo,
                shipmentId,
                withManufacturingLabels,
                true
            ) as unknown) as AnyAction
        )
    }, [dispatch, masterTrackingNo, shipmentId, withManufacturingLabels])

    const onScrollHandler = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            setShowHeaderShadow(event.currentTarget.scrollTop >= 1)
            setShowFooterShadow(
                event.currentTarget.scrollHeight - event.currentTarget.offsetHeight - event.currentTarget.scrollTop >= 1
            )
            if (onScroll) {
                onScroll(event)
            }
        },
        [onScroll]
    )

    useEffect(() => {
        dispatch(
            (TimelineActions.getSegments(
                masterTrackingNo,
                shipmentId,
                withManufacturingLabels,
                false
            ) as unknown) as AnyAction
        )
    }, [dispatch, masterTrackingNo, shipmentId, withManufacturingLabels])

    useEffect(() => {
        const mqttListener = (message: string, topic: string): void => {
            logger.debug(message, topic)
            const msg = JSON.parse(message) as Interfaces.IAnyObject

            if (msg.action === 'updated') {
                loadData()
            } else {
                dispatch((TimelineActions.updateStateByMqtt(message, topic) as unknown) as AnyAction)
            }
        }

        Services.MQTT.connect(mqttListener)

        return () => {
            if (!isSubWidget) {
                Services.MQTT.disconnect()
            } else {
                Services.MQTT.unsubscribe([`shipment/${shipmentId}`])
                Services.MQTT.removeMessageListener(mqttListener)
            }
        }
    }, [dispatch, loadData, isSubWidget])

    const Loader = useMemo((): JSX.Element | null => {
        if (!isLoading) {
            setTimeout(() => {
                if (containerRef.current) {
                    const isShowTop = containerRef.current.scrollTop >= 1
                    const isShowBottom =
                        containerRef.current.scrollHeight -
                            containerRef.current.offsetHeight -
                            containerRef.current.scrollTop >=
                        1
                    if (onLoaded) {
                        onLoaded(isShowTop, isShowBottom)
                    }
                    setShowHeaderShadow(isShowTop)
                    setShowFooterShadow(isShowBottom)
                }
            }, 100)

            return null
        }

        return Sendum.getLoader() || <Components.Loader className={s.timelineContainer__loader} text={'Loading'} />
    }, [isLoading, onLoaded])

    return (
        <div className={s.timelineContainer}>
            {showScrollShadow.top && (
                <div
                    className={`${s.timelineContainer__header} ${
                        isShowHeaderShadow ? s.timelineContainer__shadowHeader : s.timelineContainer__normalHeader
                    }`}
                />
            )}
            {Loader}
            {!isLoading && (
                <div className={s.timelineContainer__timeline} ref={containerRef} onScroll={onScrollHandler}>
                    <TimelineContent
                        noDataText={noDataText}
                        onChangeSegmentsCount={(id: string) => {
                            setTimeout(() => {
                                // eslint-disable-next-line prefer-destructuring
                                const el = document.querySelectorAll(`[data-key="${id}"]`)[0]
                                if (el) {
                                    el.scrollIntoView(false)
                                }
                            }, 10)
                        }}
                    />
                </div>
            )}
            {showScrollShadow.bottom && (
                <div
                    className={`${s.timelineContainer__footer} ${
                        isShowFooterShadow ? s.timelineContainer__shadowFooter : s.timelineContainer__normalFooter
                    }`}
                />
            )}
        </div>
    )
}
